import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError, HttpStatusCode} from '@shared/helpers/api'
import {delayTimeout} from '@shared/helpers/function'
import {isString} from '@shared/helpers/lang'
import {SchemasOrderSubscription} from '@shared/schemas/order'

import {changeLocation, systemAlert, systemConfirm} from '@web/actions/application'
import {getSavedStateFromStorage, saveStateToStorage} from '@web/actions/restoreState'
import getApi, {getGwErrorMessage} from '@web/helpers/api'
import {Sentry} from '@web/helpers/log'
import {deviceType, isMobileDisplay, osType} from '@web/helpers/userAgent'
import {chargePointSelector} from '@web/slices/chargePoint/slice'
import {commonSelector} from '@web/slices/common/slice'
import {addressesInfoSelector, deliveryInfoSelector} from '@web/slices/deliveryInfo/slice'
import {productItemDiscountInfoSelector, selectedCouponKeyInfoSelector} from '@web/slices/orderSheetCoupon/slice'
import {arrivalGuaranteeInfoSelector} from '@web/slices/orderSheetProducts/slice'
import {orderAmountInfoSelector, orderTotalSelector} from '@web/slices/orderTotal/slice'
import {membershipInfoSelector} from '@web/slices/purchaser/slice'
import {locationSelector} from '@web/slices/router'
import {shoppingSubscriptionSelector} from '@web/slices/shoppingSubscription/slice'

import oldApi from '../../apis'
import {FetchSubscriptionResponse} from '../../apis/interfaces'
import {PrevPaymentMethodInfo} from '../../apis/interfaces/subscription'
import {FailCode, RejectValue, START_SUBSCRIPTION_FALLBACK_MESSAGE, SUBSCRIPTION_BASE_PATH} from '../../constants'
import {
    FailSessionStorageInterface,
    SubscriptionRootState as SubscriptionState,
    SubscriptionThunkApi as ThunkApi,
} from '../../interfaces'
import {getRestoreState} from '../utils'

import {
    FetchDeliveryFeePayload,
    FetchDiscountsReloadPayload,
    FetchExpectedRewardPointPayload,
    FetchSubscriptionPayload,
    StartSubscriptionPayload,
} from './interfaces'
import {startSubscriptionRequestBodySelector} from './selectors'
import {getAmountInfo} from './utils'

const api = getApi('/orderApi')

const API_BASE_URL = '/orderSheet/subscription/subscription'

/**
 * 정기구독 신청서 정보 조회 액션
 * initSubscription 외부에서 미사용
 */
export const fetchSubscription = createAsyncThunk<FetchSubscriptionPayload, void, {state: SubscriptionState}>(
    'shoppingSubscription/fetchSubscription',
    async (_, {dispatch, getState, rejectWithValue}) => {
        const state = getState()
        const {seqID} = commonSelector(state)
        const {query} = locationSelector(state)

        const backUrl = query['backUrl'] ?? null

        try {
            const {paymentMethodsResult: paymentMethodsResultType, subscriptionViewResult: result} =
                await oldApi.fetchSubscription({
                    orderSheetId: seqID,
                    ...(backUrl ? {backUrl} : {}),
                })

            type ResultType = FetchSubscriptionResponse['subscriptionViewResult']

            const paymentMethodsResult: FetchSubscriptionPayload['paymentMethodsResult'] = paymentMethodsResultType
                ? // type fix 필요
                  (paymentMethodsResultType as unknown as PrevPaymentMethodInfo)
                : null
            const subscriptionViewResult: ResultType = {
                ...result,
                chargePointUser: result.chargePointUser as ResultType['chargePointUser'],
                coupon: result.coupon as unknown as ResultType['coupon'],
                delivery: result.delivery as ResultType['delivery'],
                deliveryAddressBook: result.deliveryAddressBook as ResultType['deliveryAddressBook'],
                merchantsByMerchantNo: result.merchantsByMerchantNo as ResultType['merchantsByMerchantNo'],
                products: result.products as ResultType['products'],
                sellerRegisterType: result.sellerRegisterType as ResultType['sellerRegisterType'],
                subscription: result.subscription as ResultType['subscription'],
            }

            const amountInfo = getAmountInfo(subscriptionViewResult)

            return {
                paymentMethodsResult,
                subscriptionViewResult: {
                    ...subscriptionViewResult,
                    amountInfo,
                },
            }
        } catch (e) {
            // 503 오류인 경우 middleware에서 처리한다.
            if (typeof e === 'string' && getGwErrorMessage(e)?.status === HttpStatusCode.ServiceUnavailable) {
                return rejectWithValue(e)
            }

            if (isApiError(e)) {
                if (e.response?.data?.result?.code === 'SUBSCRIPTION_ALREADY_APPLIED') {
                    dispatch(systemAlert({message: '이미 신청완료된 정기구독입니다.', type: 'ALERT'}))
                    dispatch(changeLocation({type: 'BACK'}))

                    return rejectWithValue('이미 신청 완료된 신청서에 대한 조회')
                }

                const failCode =
                    e.response?.data?.result?.validationFailure?.reasonType === FailCode.INVALID_ITEM_PAY_AMOUNT
                        ? FailCode.INVALID_ITEM_PAY_AMOUNT
                        : FailCode.SUBSCRIPTION_FETCH_FAIL

                saveStateToStorage<FailSessionStorageInterface>({
                    currentState: {failCode},
                    secureStorageKey: 'orders.paymentmethod.shopping.fail',
                    saverKey: seqID,
                })
                dispatch(changeLocation({type: 'REPLACE', url: `${SUBSCRIPTION_BASE_PATH}/${seqID}/fail`}))
            }

            Sentry.sendGeneralError(new Error('정기구독 주문서 초기화 에러'), {error: e})
            return rejectWithValue('정기구독 주문서 초기화 에러')
        }
    },
)

/**
 * 정기구독 신청서 저장된 정보 restore 액션
 * initSubscription 외부에서 미사용
 */
export const restoreSubscription = createAsyncThunk<
    {storedState: SubscriptionState; isDeliveryAddressDifferent: boolean},
    void,
    {state: SubscriptionState; rejectValue: string}
>('shoppingSubscription/restoreSubscription', async (_, {rejectWithValue, dispatch, getState}) => {
    const appState = getState()
    const {
        common: {seqID},
    } = appState
    const {query} = locationSelector(appState)

    const storedState = getSavedStateFromStorage<SubscriptionState>({saverKey: seqID, secureStorageKey: seqID})

    if (!storedState) {
        return rejectWithValue(RejectValue.RESTORE_REJECTED_BY_NO_PREV_STATE)
    }

    const {shouldRestore, isDeliveryAddressDifferent} = getRestoreState({appState, storedState})

    if (!shouldRestore) {
        return rejectWithValue(RejectValue.RESTORE_REJECTED_BY_SAME_PREV_STATE)
    }

    /** 결제수단 변경창에서 돌아왔으면 얼럿 노출 없이 바로 복원한다 */
    if (query.from === 'paymentMethodManagement') {
        return {storedState, isDeliveryAddressDifferent}
    }

    const isRestore = await new Promise<boolean>((resolve) => {
        dispatch(
            systemConfirm({
                type: 'CONFIRM',
                message: `주문서가 새로고침 되었습니다.
이전에 입력하신 정보를 그대로 유지하시겠습니까?`,
                callback: (confirmed: boolean) => {
                    resolve(confirmed)
                },
            }),
        )
    })

    if (!isRestore) {
        return rejectWithValue(RejectValue.RESTORE_REJECTED_BY_USER_CANCEL)
    }

    return {storedState, isDeliveryAddressDifferent}
})

/** 정기구독 신청서 초기화 액션 */
export const initSubscription = createAsyncThunk<
    {storedByStorage: boolean; isDeliveryChanged: boolean},
    void,
    {state: SubscriptionState}
>('shoppingSubscription/initSubscription', async (_, {dispatch}) => {
    let storedByStorage = false
    let isDeliveryChanged = false

    await dispatch(fetchSubscription()).unwrap()

    // restore 전에 api로 가져온 data로 화면을 한번 그리기 위해 timeout 추가
    await delayTimeout(0)

    try {
        await dispatch(restoreSubscription())
            .unwrap()
            .then(({isDeliveryAddressDifferent}) => {
                storedByStorage = true
                isDeliveryChanged = isDeliveryAddressDifferent
            })
    } catch {
        /** 사용자의 거절, 복원할 상태 없음등의 사유로 reject되었으므로 처리 없음 */
    }

    return {storedByStorage, isDeliveryChanged}
})

export const startSubscription = createAsyncThunk<StartSubscriptionPayload, void, ThunkApi<string>>(
    'shoppingSubscription/startSubscription',
    async (_, {getState, rejectWithValue, dispatch}) => {
        const state = getState()
        const {seqID} = commonSelector(state)

        const requestBody = startSubscriptionRequestBodySelector(state)

        if (isString(requestBody)) {
            return rejectWithValue(requestBody)
        }

        try {
            const {
                data: {result},
            } = await api.post<{result: SchemasOrderSubscription['/subscription/start']['response']}>(
                `${API_BASE_URL}/start`,
                {
                    deviceType,
                    isMobileDisplay,
                    osType,
                    startRequestBody: requestBody,
                    subscriptionSheetId: seqID,
                },
            )

            return result
        } catch (err) {
            let failMessage = START_SUBSCRIPTION_FALLBACK_MESSAGE

            if (isApiError(err)) {
                failMessage = err.response?.data.result?.fallbackMessage ?? START_SUBSCRIPTION_FALLBACK_MESSAGE
            }

            dispatch(
                systemAlert({
                    type: 'ALERT',
                    message: failMessage,
                }),
            )
            throw err
        }
    },
)

export const fetchExpectedRewardPoint = createAsyncThunk<FetchExpectedRewardPointPayload, void, ThunkApi>(
    'shoppingSubscription/fetchExpectedRewardPoint',
    async (_, {getState}) => {
        const state = getState()
        const {seqID} = commonSelector(state)
        const {
            payMoney: {userStatus, bankCode},
        } = chargePointSelector(state)
        const {subscribed} = membershipInfoSelector(state)
        const {deliveryFeeAmount} = orderAmountInfoSelector(state)
        const {
            product: selectedProductCouponIdsByOrderSheetItemId,
            duplicate: selectedProductDuplicateCouponIdByOrderSheetItemId,
            immediate: selectedImmediateDiscountPolicyNosByOrderSheetItemId,
            store: selectedStoreCouponIdsByMerchantNo,
            subscription: subscriptionDiscountSelectedOrderSheetItemIds,
        } = selectedCouponKeyInfoSelector(state)
        const {
            multiPurchaseInfo: {multiPurchaseDiscountSelectedOrderSheetItemIds},
        } = productItemDiscountInfoSelector(state)

        const requestBody = {
            chargePointUserStatus: userStatus,
            chargePointMemberType: bankCode,
            membershipSubscribed: subscribed,
            deliveryFeeAmount,
            selectedProductCouponIdsByOrderSheetItemId,
            selectedProductDuplicateCouponIdByOrderSheetItemId,
            selectedImmediateDiscountPolicyNosByOrderSheetItemId,
            selectedStoreCouponIdsByMerchantNo,
            multiPurchaseDiscountSelectedOrderSheetItemIds,
            subscriptionDiscountSelectedOrderSheetItemIds,
        }

        const {
            data: {result},
        } = await api.post<{result: SchemasOrderSubscription['/subscription/expectedRewardPoint']['response']}>(
            `${API_BASE_URL}/expectedRewardPoint`,
            {
                orderSheetId: seqID,
                requestBody,
            },
        )

        return result
    },
)

export const fetchDeliveryFee = createAsyncThunk<FetchDeliveryFeePayload, void, ThunkApi<string>>(
    'shoppingSubscription/fetchDeliveryFee',
    async (_, {getState, rejectWithValue}) => {
        try {
            const state = getState()
            const {seqID} = commonSelector(state)
            const {
                selected: {addressInfo},
            } = deliveryInfoSelector(state)
            const {deliveryFeeAmount} = orderTotalSelector(state)
            const {arrivalGuaranteesByOrderSheetProductId} = arrivalGuaranteeInfoSelector(state)
            const {isImmediatePayment} = shoppingSubscriptionSelector(state)

            // selector의 data는 rmid, outboundLocationId가 nullable인데, api 요청시에는 undefined로 변환해줘야한다
            const convertedArrivalGuaranteesByOrderSheetProductId = Object.entries(
                arrivalGuaranteesByOrderSheetProductId,
            ).reduce<
                SchemasOrderSubscription['/subscription/deliveryFee']['request']['arrivalGuaranteesByOrderSheetProductId']
            >((acc, [key, value]) => {
                acc[key] = {
                    rmid: value.rmid ?? undefined,
                    arrivalGuaranteeDateTime: value.arrivalGuaranteeDateTime,
                    outboundLocationId: value.outboundLocationId ?? undefined,
                    deliveryTagType: value.deliveryTagType,
                }
                return acc
            }, {})

            if (!addressInfo) {
                return rejectWithValue(RejectValue.NO_SELECTED_ADDRESS)
            }

            const {zipCode, buildingManagementNo, locationX, locationY} = addressInfo

            const {
                data: {result},
            } = await api.post<{result: SchemasOrderSubscription['/subscription/deliveryFee']['response']}>(
                `${API_BASE_URL}/deliveryFee`,
                {
                    orderSheetId: seqID,
                    zipCode,
                    ...(buildingManagementNo ? {buildingManagementNo} : {}),
                    ...(locationX ? {locationX} : {}),
                    ...(locationY ? {locationY} : {}),
                    arrivalGuaranteesByOrderSheetProductId: convertedArrivalGuaranteesByOrderSheetProductId,
                },
            )

            const arrivalDateChangedProductIds: string[] = []

            const updatedArrivalGuaranteesByOrderSheetProductId = result.updatedArrivalGuaranteesByOrderSheetProductId

            // N배송 속성이 변경되었는데 변경된 값이 도착보장 시간 혹은 배송 태그라면 도착보장 속성 변경된 상품 목록에 추가
            if (updatedArrivalGuaranteesByOrderSheetProductId) {
                Object.entries(arrivalGuaranteesByOrderSheetProductId).forEach(
                    ([productId, {arrivalGuaranteeDateTime, deliveryTagType}]) => {
                        const updatedArrivalGuaranteeInfo = updatedArrivalGuaranteesByOrderSheetProductId[productId]

                        if (
                            updatedArrivalGuaranteeInfo &&
                            (updatedArrivalGuaranteeInfo.arrivalGuaranteeDateTime !== arrivalGuaranteeDateTime ||
                                updatedArrivalGuaranteeInfo.deliveryTagType !== deliveryTagType)
                        ) {
                            arrivalDateChangedProductIds.push(productId)
                        }
                    },
                )
            }

            const isDeliveryFeeChanged = deliveryFeeAmount !== result.delivery.totalPrepayOrderDeliveryFee

            return {...result, isDeliveryFeeChanged, arrivalDateChangedProductIds, isImmediatePayment}
        } catch (e) {
            if (isApiError(e)) {
                return rejectWithValue(e.response?.data?.result)
            }
            throw e
        }
    },
)

export const fetchDiscountsReload = createAsyncThunk<
    {
        coupon: FetchDiscountsReloadPayload
    },
    void,
    ThunkApi
>('shoppingSubscription/fetchDiscountsReload', async (_, {getState, rejectWithValue}) => {
    try {
        const state = getState()
        const {seqID} = commonSelector(state)
        const {deliveryAddress} = addressesInfoSelector(state)

        const {
            data: {result},
        } = await api.post<{result: SchemasOrderSubscription['/subscription/discountsReload']['response']}>(
            `${API_BASE_URL}/discountsReload`,
            {
                subscriptionSheetId: seqID,
                request: {
                    zipCode: deliveryAddress.zipCode,
                    buildingManagementNo: deliveryAddress.buildingManagementNo,
                },
            },
        )

        return {
            coupon: result,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})
